import React from "react";
import { useTheme } from "@emotion/react";
import { AppTheme } from "../../utils/theme";
import { Text as RNText, TextStyle, LayoutChangeEvent } from "react-native";
import { isArray } from "lodash";

const MINIMUM_BODY_SIZE = 16;
const MINIMUM_BODY_LINE_HEIGHT = 20;

export type TextType =
    | "default"
    | "h1"
    | "h2"
    | "h3"
    | "h4"
    | "h5"
    | "h6"
    | "h7"
    | "h8"
    | "h9"
    | "h10"
    | "body1"
    | "body2"
    | "body3"
    | "body4"
    | "body5"
    | "cta1"
    | "cta2"
    | "cta3"
    | "ctah2"
    | "ctah3"
    | "ctah4"
    | "ctah5"
    | "caption1"
    | "keypad"
    | "alert"
    | "mono1"
    | "mono2"
    | "mono3"
    | "mono4";

type TextProps = {
    type?: TextType;
    style?: TextStyle;
    onLayout?: (event: LayoutChangeEvent) => void;
    children: any;
    allowFontScaling?: boolean;
};

const Text = ({
    children,
    type,
    style,
    onLayout,
    allowFontScaling = true,
}: TextProps) => {
    // const theme: AppTheme = useTheme() as AppTheme;
    const {
        textStyles,
        color: {
            accent,
            heading,
            text,
            ctaText,
            ctaText2,
            pinText,
            secondaryAccent,
            background,
        },
        verticalScale,
    } = useTheme() as AppTheme;
    let _style: TextStyle;

    switch (type) {
        case "h1":
            _style = {
                ...textStyles.h1,
                color: heading,
            };
            break;

        case "h2":
            _style = {
                ...textStyles.h2,
                color: heading,
            };
            break;

        case "h3":
            _style = {
                ...textStyles.h3,
                color: heading,
            };
            break;

        case "h4":
            _style = {
                ...textStyles.h4,
                color: heading,
            };
            break;
        case "h5":
            _style = {
                ...textStyles.h5,
                color: heading,
            };
            break;

        case "h6":
            _style = {
                ...textStyles.h6,
                color: heading,
            };
            break;
        case "h7":
            _style = {
                ...textStyles.h7,
                color: heading,
            };
            break;

        case "h8":
            _style = {
                ...textStyles.h8,
                color: heading,
            };
            break;

        case "h9":
            _style = {
                ...textStyles.h9,
                color: heading,
            };
            break;

        case "h10":
            _style = {
                ...textStyles.h10,
                color: heading,
            };
            break;

        case "body1":
            _style = {
                ...textStyles.body1,
                color: text,
            };
            break;

        case "body2":
            _style = {
                ...textStyles.body2,
                color: text,
            };
            break;

        case "body3":
            _style = {
                ...textStyles.body3,
                color: text,
            };
            break;

        case "body4":
            _style = {
                ...textStyles.body4,
                color: text,
            };
            break;

        case "body5":
            _style = {
                ...textStyles.body5,
                color: secondaryAccent,
            };
            break;

        case "mono1":
            _style = {
                ...textStyles.mono1,
                color: heading,
            };
            break;

        case "mono2":
            _style = {
                ...textStyles.mono2,
                color: text,
            };
            break;

        case "mono3":
            _style = {
                ...textStyles.mono3,
                color: text,
            };
            break;

        case "mono4":
            _style = {
                ...textStyles.mono4,
                color: text,
            };
            break;

        case "cta1":
            _style = {
                ...textStyles.cta1,
                color: accent,
            };
            break;

        case "cta2":
            _style = {
                ...textStyles.cta2,
                color: text,
            };
            break;

        case "cta3":
            _style = {
                ...textStyles.cta3,
                color: ctaText,
            };
            break;

        case "ctah2":
            _style = {
                ...textStyles.ctah2,
                color: ctaText,
            };
            break;

        case "ctah3":
            _style = {
                ...textStyles.ctah3,
                color: text,
            };
            break;

        case "ctah4":
            _style = {
                ...textStyles.ctah4,
                color: ctaText,
            };
            break;

        case "ctah5":
            _style = {
                ...textStyles.ctah5,
                color: ctaText2,
            };
            break;

        case "caption1":
            _style = {
                ...textStyles.caption1,
                color: text,
            };
            break;

        case "keypad":
            _style = {
                ...textStyles.keypad,
                color: background,
            };
            break;
        case "alert":
            _style = {
                ...textStyles.alert,
                color: pinText,
            };
            break;
        default:
            _style = {
                ...textStyles.default,
                color: text,
            };
    }

    if (isArray(style)) {
        _style = {
            ..._style,
            ...style.reduce(
                (acc, innerStyle) => ({ ...acc, ...innerStyle }),
                {},
            ),
        };
    } else {
        _style = {
            ..._style,
            ...style,
        };
    }

    if (type && verticalScale < 1) {
        switch (type) {
            case "body1":
            case "body2":
            case "body3":
            case "body4":
            case "caption1":
                break;

            default:
                {
                    let scaledFontSize = _style.fontSize
                        ? _style.fontSize * verticalScale
                        : null;
                    let scaledLineHeight = _style.lineHeight
                        ? _style.lineHeight * verticalScale
                        : null;

                    if (scaledFontSize < MINIMUM_BODY_SIZE) {
                        scaledFontSize = MINIMUM_BODY_SIZE;
                    }

                    if (scaledLineHeight < MINIMUM_BODY_LINE_HEIGHT) {
                        scaledLineHeight = MINIMUM_BODY_LINE_HEIGHT;
                    }

                    _style = {
                        ..._style,
                        fontSize: scaledFontSize,
                        lineHeight: scaledLineHeight,
                    };
                }

                break;
        }
    }

    return (
        <RNText
            style={_style}
            onLayout={onLayout}
            allowFontScaling={allowFontScaling}>
            {children}
        </RNText>
    );
};

export default Text;
